import type { Transaction, TransactionType } from "@/hooks/useTransactions";

/**
 * Every bucket is decided by a structural link on the transaction itself — an installment,
 * a saving plan/pocket, or the transaction type. Category names are deliberately never
 * read: one user writes "Savings" and the next writes "Marranito", and a rule built on
 * that breaks for everyone but its author.
 */
export type SpendBucket = "committed" | "saved" | "movement" | "discretionary";

export type ClassifiedSpend = {
  bucket: SpendBucket;
  amount: number;
  categoryId: number | null;
};

export type CycleSpendBreakdown = {
  committed: number;
  saved: number;
  movement: number;
  discretionary: number;
  /** What the user actually spent this cycle: money that left, not money that moved. */
  totalSpend: number;
  discretionaryByCategory: Map<number | null, number>;
};

const EMPTY_BREAKDOWN: CycleSpendBreakdown = {
  committed: 0,
  saved: 0,
  movement: 0,
  discretionary: 0,
  totalSpend: 0,
  discretionaryByCategory: new Map(),
};

const classifyTransaction = (
  transaction: Transaction,
  typeNameById: Map<number, string>,
): ClassifiedSpend | null => {
  const typeName = typeNameById.get(transaction.type_id);

  if (typeName !== "EXPENSE" && typeName !== "TRANSFER") return null;

  const amount = Math.abs(Number(transaction.amount ?? 0));
  if (!Number.isFinite(amount) || amount === 0) return null;

  const categoryId = transaction.category_id ?? null;

  if (transaction.installment_id != null) {
    return { bucket: "committed", amount, categoryId };
  }

  if (transaction.saving_plan_id != null || transaction.saving_pocket_id != null) {
    return { bucket: "saved", amount, categoryId };
  }

  if (typeName === "TRANSFER") {
    return { bucket: "movement", amount, categoryId };
  }

  return { bucket: "discretionary", amount, categoryId };
};

export const buildCycleSpendBreakdown = (
  transactions: Transaction[],
  transactionTypes: TransactionType[],
): CycleSpendBreakdown => {
  if (transactions.length === 0 || transactionTypes.length === 0) {
    return EMPTY_BREAKDOWN;
  }

  const typeNameById = new Map(transactionTypes.map((type) => [type.id, type.name]));
  const discretionaryByCategory = new Map<number | null, number>();
  let committed = 0;
  let saved = 0;
  let movement = 0;
  let discretionary = 0;

  transactions.forEach((transaction) => {
    const classified = classifyTransaction(transaction, typeNameById);
    if (!classified) return;

    switch (classified.bucket) {
      case "committed":
        committed += classified.amount;
        break;
      case "saved":
        saved += classified.amount;
        break;
      case "movement":
        movement += classified.amount;
        break;
      case "discretionary":
        discretionary += classified.amount;
        discretionaryByCategory.set(
          classified.categoryId,
          (discretionaryByCategory.get(classified.categoryId) ?? 0) + classified.amount,
        );
        break;
    }
  });

  return {
    committed,
    saved,
    movement,
    discretionary,
    totalSpend: committed + discretionary,
    discretionaryByCategory,
  };
};

export type DiscretionaryBudgetCategory = {
  id: number;
  name: string;
  spent: number;
  budgetAmount: number;
  overage: number;
};

type BudgetLike = { category_id?: number | null; amount?: number };
type CategoryLike = { id: number; name: string };

/**
 * Budget pressure measured only on what the user can still decide about. A category whose
 * whole cycle is committed spending has no discretionary spend, so it can no longer headline an
 * action the user has no way to take.
 */
export const buildDiscretionaryBudgetPressure = (
  discretionaryByCategory: Map<number | null, number>,
  budgets: BudgetLike[],
  categories: CategoryLike[],
) => {
  const categoryNameById = new Map(categories.map((category) => [category.id, category.name]));
  const overspent: DiscretionaryBudgetCategory[] = [];
  let budgetedSpend = 0;

  budgets.forEach((budget) => {
    if (typeof budget.category_id !== "number") return;

    const budgetAmount = Number(budget.amount ?? 0);
    if (budgetAmount <= 0) return;

    const spent = discretionaryByCategory.get(budget.category_id) ?? 0;
    budgetedSpend += spent;

    if (spent <= budgetAmount) return;

    overspent.push({
      id: budget.category_id,
      name: categoryNameById.get(budget.category_id) ?? String(budget.category_id),
      spent,
      budgetAmount,
      overage: spent - budgetAmount,
    });
  });

  overspent.sort((first, second) => second.overage - first.overage);

  const totalDiscretionary = [...discretionaryByCategory.values()].reduce(
    (sum, amount) => sum + amount,
    0,
  );

  return {
    overspentCategories: overspent,
    topOverspentCategory: overspent[0] ?? null,
    totalOverage: overspent.reduce((sum, category) => sum + category.overage, 0),
    /** Discretionary spend the user never set a limit for — shown, not silently absorbed. */
    unbudgetedSpend: Math.max(totalDiscretionary - budgetedSpend, 0),
  };
};
